import { env } from '@/shared/config/env';
import { ApiError, extractErrorDetail } from './ApiError';

/**
 * Typ urzadzenia wysylany w naglowku `X-Device-Type`.
 * Odpowiada enumowi `pl.tenify.common.security.DeviceType`.
 */
const DEVICE_TYPE = 'WEB';

/**
 * Punkty zaczepienia, przez ktore modul uwierzytelniania wstrzykuje swoja logike.
 * Dzieki temu `httpClient` nie zalezy od modulu `features/auth` — zaleznosc idzie
 * tylko w jedna strone i nie powstaje cykl importow.
 */
export interface HttpAuthBridge {
  /** Zwraca aktualny token dostepu albo `null`, jesli uzytkownik nie jest zalogowany. */
  getAccessToken: () => string | null;
  /** Zwraca identyfikator sesji do naglowka `X-Session-Id`. */
  getSessionId: () => string | null;
  /** Probuje odswiezyc token. Zwraca nowy token albo `null`, gdy sie nie udalo. */
  refreshAccessToken: () => Promise<string | null>;
  /** Wywolywane, gdy odswiezenie zawiodlo i trzeba wylogowac uzytkownika. */
  onAuthenticationLost: () => void;
}

const noopBridge: HttpAuthBridge = {
  getAccessToken: () => null,
  getSessionId: () => null,
  refreshAccessToken: () => Promise.resolve(null),
  onAuthenticationLost: () => undefined,
};

let bridge: HttpAuthBridge = noopBridge;

/** Podlacza logike uwierzytelniania do klienta HTTP. Wolane raz, przy starcie aplikacji. */
export function configureHttpClient(next: HttpAuthBridge): void {
  bridge = next;
}

/** Przywraca stan poczatkowy — uzywane w testach. */
export function resetHttpClient(): void {
  bridge = noopBridge;
  refreshInFlight = null;
}

/**
 * Trwajace odswiezanie tokena. Gdy kilka zadan dostanie 401 jednoczesnie,
 * wszystkie czekaja na to samo wywolanie `/api/auth/refresh` zamiast
 * uruchamiac je rownolegle (i uniewazniac sobie nawzajem refresh token).
 */
let refreshInFlight: Promise<string | null> | null = null;

function refreshOnce(): Promise<string | null> {
  refreshInFlight ??= bridge.refreshAccessToken().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

export interface RequestOptions extends Omit<RequestInit, 'body' | 'method'> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Cialo zadania — serializowane do JSON-a, chyba ze to `FormData`. */
  body?: unknown;
  /** Parametry zapytania. Pola `undefined` i `null` sa pomijane. */
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Pomija doklejanie naglowka `Authorization` (endpointy publiczne). */
  skipAuth?: boolean;
  /** Pomija probe odswiezenia tokena po odpowiedzi 401. */
  skipRefresh?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${env.apiBaseUrl}${path}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null) {
      params.append(key, String(value));
    }
  }

  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
}

/** Czyta cialo odpowiedzi jako JSON, a gdy to nie JSON — jako tekst. */
async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return undefined;

  const text = await response.text();
  if (text.length === 0) return undefined;

  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  }
  return text;
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const { method = 'GET', body, query, skipAuth, headers, ...rest } = options;

  const requestHeaders = new Headers(headers);
  requestHeaders.set('Accept', 'application/json, text/plain;q=0.9, */*;q=0.8');
  requestHeaders.set('X-Device-Type', DEVICE_TYPE);

  const sessionId = bridge.getSessionId();
  if (sessionId) {
    requestHeaders.set('X-Session-Id', sessionId);
  }

  if (!skipAuth) {
    const token = bridge.getAccessToken();
    if (token) {
      requestHeaders.set('Authorization', `Bearer ${token}`);
    }
  }

  const isFormData = body instanceof FormData;
  if (body !== undefined && !isFormData && !requestHeaders.has('Content-Type')) {
    requestHeaders.set('Content-Type', 'application/json');
  }

  return fetch(buildUrl(path, query), {
    ...rest,
    method,
    headers: requestHeaders,
    body: body === undefined ? null : isFormData ? body : JSON.stringify(body),
  });
}

/**
 * Wykonuje zadanie HTTP do API Tenify.
 *
 * Doklada naglowki kontekstu (`Authorization`, `X-Session-Id`, `X-Device-Type`),
 * a po odpowiedzi 401 raz probuje odswiezyc token i powtorzyc zadanie.
 *
 * @throws {ApiError} dla odpowiedzi spoza zakresu 2xx oraz dla bledow sieci
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response: Response;

  try {
    response = await send(path, options);
  } catch (cause) {
    throw new ApiError(0, 'Nie udało się połączyć z serwerem.', undefined, cause);
  }

  // 401 — sprobuj odswiezyc token i powtorzyc zadanie dokladnie raz.
  if (response.status === 401 && !options.skipAuth && !options.skipRefresh) {
    const refreshedToken = await refreshOnce();

    if (refreshedToken) {
      try {
        response = await send(path, { ...options, skipRefresh: true });
      } catch (cause) {
        throw new ApiError(0, 'Nie udało się połączyć z serwerem.', undefined, cause);
      }
    } else {
      bridge.onAuthenticationLost();
    }
  }

  const payload = await readBody(response);

  if (!response.ok) {
    const detail = extractErrorDetail(payload);
    throw new ApiError(
      response.status,
      detail ?? `Żądanie zakończyło się błędem ${response.status}.`,
      detail,
      payload,
    );
  }

  return payload as T;
}

/** Skroty na najczestsze metody HTTP. */
export const http = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
} as const;
